/**
 * useGateway Hook
 *
 * Manages the gateway background service within the Ink interactive UI.
 * Listens for remote requests and translates gateway events into
 * messages/state that the App can render alongside local interactions.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { GatewayService, type GatewayEvent } from '../../gateway/service.js';
import type { ToolCallRecord } from '../components/ToolCallIndicator.js';

export interface RemoteRequest {
    requestId: string;
    clientType: string;
    message: string;
}

export interface UseGatewayOptions {
    enabled: boolean;
    pollInterval?: number;
    sessionName?: string;
    debug?: boolean;
    mcpState?: unknown;
    onMcpToolCall?: unknown;
    mcpClient?: unknown;
}

export interface CompletedRemoteExchange {
    userMessage: string;
    assistantContent: string;
    clientType: string;
}

export interface UseGatewayReturn {
    isOnline: boolean;
    isProcessingRemote: boolean;
    remoteRequest: RemoteRequest | null;
    remoteStreamingText: string;
    remoteToolCallHistory: ToolCallRecord[];
    remoteError: string | null;
    completedCount: number;
    statusMessage: string | null;
    lastCompletedExchange: CompletedRemoteExchange | null;
    clearCompletedExchange: () => void;
}

export function useGateway(options: UseGatewayOptions): UseGatewayReturn {
    const { enabled, pollInterval = 3000, sessionName, debug = false, mcpState, onMcpToolCall, mcpClient } = options;

    const [isOnline, setIsOnline] = useState(false);
    const [isProcessingRemote, setIsProcessingRemote] = useState(false);
    const [remoteRequest, setRemoteRequest] = useState<RemoteRequest | null>(null);
    const [remoteStreamingText, setRemoteStreamingText] = useState('');
    const [remoteToolCallHistory, setRemoteToolCallHistory] = useState<ToolCallRecord[]>([]);
    const [remoteError, setRemoteError] = useState<string | null>(null);
    const [completedCount, setCompletedCount] = useState(0);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [lastCompletedExchange, setLastCompletedExchange] = useState<CompletedRemoteExchange | null>(null);

    const gatewayRef = useRef<GatewayService | null>(null);
    const streamingTextRef = useRef('');
    const activeRequestRef = useRef<RemoteRequest | null>(null);

    const trackToolCall = useCallback((id: string, name: string, type: 'tool' | 'mcp', status: 'pending' | 'completed' | 'error', serverName?: string) => {
        setRemoteToolCallHistory(prev => {
            const existing = prev.findIndex(t => t.id === id);
            if (existing >= 0) {
                const updated = [...prev];
                updated[existing] = { ...updated[existing], status };
                return updated;
            }
            return [...prev, { id, name, type, serverName, status, timestamp: Date.now() }];
        });
    }, []);

    useEffect(() => {
        if (!enabled) return;

        const gateway = new GatewayService({ pollInterval, debug, sessionName });

        if (mcpState) {
            gateway.setMcpState(mcpState, onMcpToolCall, mcpClient);
        }

        gatewayRef.current = gateway;

        const handleEvent = (event: GatewayEvent) => {
            switch (event.type) {
                case 'started':
                    setIsOnline(true);
                    setStatusMessage(null);
                    break;

                case 'stopped':
                    setIsOnline(false);
                    setIsProcessingRemote(false);
                    setRemoteRequest(null);
                    break;

                case 'request_claimed': {
                    const req = {
                        requestId: event.request.requestId,
                        clientType: event.request.clientType,
                        message: event.request.message,
                    };
                    setIsProcessingRemote(true);
                    setRemoteRequest(req);
                    activeRequestRef.current = req;
                    setRemoteStreamingText('');
                    setRemoteToolCallHistory([]);
                    setRemoteError(null);
                    streamingTextRef.current = '';
                    break;
                }

                case 'remote_content':
                    if (event.partial) {
                        streamingTextRef.current += event.text;
                    } else {
                        streamingTextRef.current = event.text;
                    }
                    setRemoteStreamingText(streamingTextRef.current);
                    break;

                case 'remote_tool_call':
                    trackToolCall(event.id, event.name, event.isMcp ? 'mcp' : 'tool', 'pending', event.serverName);
                    break;

                case 'remote_tool_result':
                    trackToolCall(event.id, event.name, 'tool', event.status, undefined);
                    break;

                case 'remote_error':
                    setRemoteError(event.error);
                    break;

                case 'remote_done': {
                    const finishedReq = activeRequestRef.current;
                    if (finishedReq && streamingTextRef.current) {
                        setLastCompletedExchange({
                            userMessage: finishedReq.message,
                            assistantContent: streamingTextRef.current,
                            clientType: finishedReq.clientType,
                        });
                    }
                    setIsProcessingRemote(false);
                    setRemoteRequest(null);
                    setRemoteStreamingText('');
                    setRemoteToolCallHistory([]);
                    activeRequestRef.current = null;
                    streamingTextRef.current = '';
                    break;
                }

                case 'request_completed':
                    setCompletedCount(prev => prev + 1);
                    break;

                case 'request_failed':
                    setRemoteError(event.error);
                    setIsProcessingRemote(false);
                    setRemoteRequest(null);
                    break;

                case 'error':
                    if (typeof event.error === 'string' && event.error.startsWith('Heartbeat failed')) {
                        setStatusMessage(event.error);
                    }
                    break;

                case 'poll_empty':
                    break;
            }
        };

        gateway.on('event', handleEvent);

        gateway.start().catch((err) => {
            setStatusMessage(`Gateway failed: ${err instanceof Error ? err.message : String(err)}`);
        });

        return () => {
            gateway.removeListener('event', handleEvent);
            gateway.stop().catch(() => {});
            gatewayRef.current = null;
        };
    }, [enabled, pollInterval, sessionName, debug, mcpState, onMcpToolCall, mcpClient, trackToolCall]);

    const clearCompletedExchange = useCallback(() => {
        setLastCompletedExchange(null);
    }, []);

    return {
        isOnline,
        isProcessingRemote,
        remoteRequest,
        remoteStreamingText,
        remoteToolCallHistory,
        remoteError,
        completedCount,
        statusMessage,
        lastCompletedExchange,
        clearCompletedExchange,
    };
}
